import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../../config';
import './PortScanner.css';

const PortScanner = () => {
  const { t } = useTranslation();

  const [host, setHost] = useState('');
  const [ports, setPorts] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const parsePorts = (portsStr) => {
    const portSet = new Set();

    portsStr.split(',').forEach(part => {
      part = part.trim();

      if (part.includes('-')) {
        const [start, end] = part.split('-').map(Number);
        if (!isNaN(start) && !isNaN(end) && start <= end) {
          for (let p = start; p <= end; p++) {
            portSet.add(p);
          }
        }
      } else {
        const p = parseInt(part, 10);
        if (!isNaN(p)) portSet.add(p);
      }
    });

    return Array.from(portSet).sort((a, b) => a - b);
  };

  const handleScan = async () => {
    if (!host.trim()) {
      return alert(t('enterValidHost'));
    }

    let portArray = [];
    let isCustom = false;

    if (ports.trim()) {
      portArray = parsePorts(ports);
      if (portArray.length === 0) {
        return alert(t('enterValidPorts'));
      }
      isCustom = true;
    } else {
      // Default common ports range or full scan
      portArray = Array.from(
        { length: 1024 - 22 + 1 },
        (_, i) => i + 22
      );
    }

    setLoading(true);
    setResults([]); // Clear previous results

    try {
      const response = await fetch(`${API_BASE_URL}/diagnostic-tools/portscan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          host,
          ports: portArray,
          isCustom
        }),
      });

      if (!response.ok) {
        throw new Error('Scan failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        
        // Keep unfinished line in buffer
        buffer = lines.pop(); 

        for (const line of lines) {
          if (line.trim()) {
            const parsedResult = JSON.parse(line);
            setResults(prev => [...prev, parsedResult]);
          }
        }
      }

      // Process remaining buffer
      if (buffer.trim()) {
        const parsedResult = JSON.parse(buffer);
        setResults(prev => [...prev, parsedResult]);
      }

    } catch (err) {
      console.error(err);
      alert(t('scanFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="portscan-container">
      <h2 className="portscan-title">
        🛠️ {t('portScanner')}
      </h2>

      <input
        className="portscan-input"
        placeholder={t('hostPlaceholder')}
        value={host}
        onChange={(e) => setHost(e.target.value)}
      />

      <input
        className="portscan-input"
        placeholder={t('portsPlaceholder')}
        value={ports}
        onChange={(e) => setPorts(e.target.value)}
      />

      <button
        className="portscan-button"
        onClick={handleScan}
        disabled={loading}
      >
        {loading ? t('scanning') : t('scan')}
      </button>

      {results.length > 0 && (
        <div className="portscan-results">
          <table className="portscan-table">
            <thead>
              <tr>
                <th>{t('port')}</th>
                <th>{t('status')}</th>
                <th>{t('service')}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, idx) => (
                <tr key={idx} className={`status-${r.status}`}>
                  <td>{r.port}</td>
                  <td>{t(r.status)}</td>
                  <td>{r.service}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PortScanner;